"use client"

import MuiTab from "@mui/material/Tab"
import MuiTabs from "@mui/material/Tabs"
import Link from "next/link"
import { useRouter, usePathname } from "next/navigation"
import { useCallback, useEffect, useMemo, useState } from "react"
import type { components } from "umbraco"
import { getLocalizedUrl } from "utils"
import Container from "../../Container"
import SearchParamsListener from "../../common/SearchParamsListener"
import ArrowLeft from "../../icons/ArrowLeft"
import ChevronBullet from "../../icons/ChevronBullet"
import ChevronLeft from "../../icons/ChevronLeft"
import ChevronRight from "../../icons/ChevronRight"
import LocationPin from "../../icons/LocationPin"
import {
  BoldCount,
  CardContent,
  CardExcerpt,
  CardFooter,
  CardGrid,
  CardHeading,
  CardTitle,
  Chip,
  ChipText,
  ChipsContainer,
  ChevronWrapper,
  CtaArrow,
  CtaLink,
  CtaText,
  FilterContainer,
  FilterLabel,
  InnerHero,
  JobCard,
  LocationIcon,
  LocationRow,
  LocationText,
  PageButton,
  PageButtonText,
  PageSubtitle,
  PageTitle,
  PaginationArrow,
  PaginationContainer,
  Root,
  TextContainer,
  TitleRow,
  TotalCounter,
} from "./styles"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type CareerLobbyData = components["schemas"]["CareerLobbyPropertiesModel"]

interface JobItem {
  title: string
  excerpt: string
  location: string
  category: string
  lastUpdated: string
  /** Umbraco document updateDate — always a valid ISO date, used as sort fallback */
  updateDate?: string
  slug: string
  isActive: boolean
}

interface Props {
  data: CareerLobbyData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  /** Job listings injected from the page-level loader / parent */
  jobListings?: JobItem[]
  [key: string]: unknown
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const ALL_KEY = "__all__"
const DEFAULT_ITEMS_PER_PAGE = 6

/**
 * EGDE-45: Translation maps for CMS dropdown values.
 * Umbraco Dropdown data types store fixed English preset values. Since these
 * values are language-invariant, we translate them to the display locale here.
 * Keys = English CMS values, values = per-locale display labels.
 */
const CATEGORY_LABELS: Record<string, Record<string, string>> = {
  he: {
    Drivers: "נהגים",
    Instructors: "מדריכים",
    Office: "משרד",
  },
  ar: {
    Drivers: "سائقين",
    Instructors: "مرشدين",
    Office: "مكتب",
  },
  ru: {
    Drivers: "Водители",
    Instructors: "Инструкторы",
    Office: "Офис",
  },
}

const REGION_LABELS: Record<string, Record<string, string>> = {
  he: {
    "Gush Dan & Center": "גוש דן ומרכז",
    Shfela: "שפלה",
    South: "דרום",
    North: "צפון",
    "Jerusalem Area": "אזור ירושלים",
  },
  ar: {
    "Gush Dan & Center": "غوش دان والمركز",
    Shfela: "الشفلة",
    South: "الجنوب",
    North: "الشمال",
    "Jerusalem Area": "منطقة القدس",
  },
  ru: {
    "Gush Dan & Center": "Гуш-Дан и Центр",
    Shfela: "Шфела",
    South: "Юг",
    North: "Север",
    "Jerusalem Area": "Район Иерусалима",
  },
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Translate an English CMS dropdown value to the current locale display label. */
function translateLabel(
  value: string,
  map: Record<string, Record<string, string>>,
  locale: string,
): string {
  // English locale — display the original CMS value as-is
  if (locale === "en") return value
  // Strip culture suffix: "he-IL" → "he"
  const lang = locale.split("-")[0]
  return map[lang]?.[value] ?? value
}

/**
 * Build a sliding window of page numbers (max 5 visible).
 * No ellipsis — just a contiguous window of up to 5 pages.
 */
function getPageRange(currentPage: number, totalPages: number): number[] {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  let start = Math.max(1, currentPage - 2)
  const end = Math.min(totalPages, start + 4)
  start = Math.max(1, end - 4)

  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function CareerLobby({
  data,
  locale,
  culture,
  jobListings,
}: Props) {
  const isRtl = culture === "he-il" || culture === "he-IL"
  // See <SearchParamsListener>: reading useSearchParams() directly here would
  // bail this block out of static rendering, so the route could not be
  // prerendered. Empty on the server, populated on mount.
  const [searchParams, setSearchParams] = useState<URLSearchParams>(
    () => new URLSearchParams(),
  )
  const router = useRouter()
  const pathname = usePathname()

  const allLabel = isRtl ? "הכל" : "All"

  // Region filter from CMS — filter out "All" since we hardcode it
  const allRegionLabel = isRtl ? "הכל" : "All"
  const regionOptions = (data.regionsOptions ?? []).filter(
    (r) => r !== allRegionLabel,
  )
  const regionsLabel = data.regionsLabel ?? ""

  // CTA label
  const ctaLabel = isRtl ? "לפרטי המשרה" : "Job details"

  // Total counter label template
  const totalLabelTemplate = isRtl
    ? 'סה"כ {count} משרות פנויות'
    : "Total {count} open positions"

  // Items per page
  const itemsPerPage = data.itemsPerPage ?? DEFAULT_ITEMS_PER_PAGE

  // State
  const [activeCategory, setActiveCategory] = useState(ALL_KEY)
  const [activeRegions, setActiveRegions] = useState<Set<string>>(
    new Set([ALL_KEY]),
  )
  const [currentPage, setCurrentPage] = useState(1)

  // Sync state with URL search params on mount and URL changes
  useEffect(() => {
    const pageParam = searchParams.get("page")
    const parsed = pageParam ? parseInt(pageParam, 10) : 1
    const page = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1
    setCurrentPage(page)

    const categoryParam = searchParams.get("category")
    if (categoryParam && categoryOptions.includes(categoryParam)) {
      setActiveCategory(categoryParam)
    } else if (!categoryParam) {
      setActiveCategory(ALL_KEY)
    }

    const regionParams = searchParams.getAll("region")
    const validRegions = regionParams.filter((r) => regionOptions.includes(r))
    if (validRegions.length > 0) {
      setActiveRegions(new Set(validRegions))
    } else {
      setActiveRegions(new Set([ALL_KEY]))
    }
  }, [searchParams]) // eslint-disable-line react-hooks/exhaustive-deps

  // Use injected job listings, sorted by date (closest/most recent first).
  // Prefer lastUpdated (Date Picker ISO value); fall back to Umbraco
  // document updateDate when lastUpdated is missing or unparseable.
  // ESEIM-305 asked for date order; EGDE-737 asked for the editor's Umbraco
  // order. Both hold: Array.prototype.sort is stable, so jobs sharing a date
  // (today every one of them does) keep the order the loader fetched them in,
  // which is now sortOrder. Do not replace this with an unstable comparator.
  const allJobs = useMemo(() => {
    const active = (jobListings ?? []).filter((job) => job.isActive)
    return active.sort((a, b) => {
      const parseDate = (job: JobItem): number => {
        if (job.lastUpdated) {
          const t = new Date(job.lastUpdated).getTime()
          if (!isNaN(t)) return t
        }
        if (job.updateDate) {
          const t = new Date(job.updateDate).getTime()
          if (!isNaN(t)) return t
        }
        return 0
      }
      return parseDate(b) - parseDate(a) // descending — most recent first
    })
  }, [jobListings])

  // EGDE-45: Category tabs are keyed by their DISPLAY LABEL and deduped, so each
  // category renders exactly once even when the job source mixes raw values that
  // resolve to the same label (e.g. English "Drivers" and Hebrew "נהגים" both →
  // נהגים, which previously produced two identical tabs). Prefer the CMS-curated
  // categoryOptions when set, otherwise auto-surface from the active job
  // postings; either way collapse to distinct translated labels.
  const categoryOptions = useMemo(() => {
    const cmsOptions = (data.categoryOptions ?? []).filter(Boolean)
    const rawValues =
      cmsOptions.length > 0
        ? cmsOptions
        : allJobs.map((job) => job.category).filter(Boolean)

    const seen = new Set<string>()
    const labels: string[] = []
    for (const raw of rawValues) {
      const label = translateLabel(raw, CATEGORY_LABELS, locale)
      if (!seen.has(label)) {
        seen.add(label)
        labels.push(label)
      }
    }
    return labels
  }, [data.categoryOptions, allJobs, locale])

  // ESEIM-296: Region filter is multi-select.
  // "All" means no region constraint; selecting specific regions filters to
  // jobs matching ANY of the selected regions.
  const isAllRegions = activeRegions.has(ALL_KEY)

  // Filter jobs
  const filteredJobs = useMemo(() => {
    let jobs = allJobs

    if (activeCategory !== ALL_KEY) {
      // EGDE-45: match by translated label so a tab catches every raw category
      // value that maps to it (mixed English/Hebrew job data).
      jobs = jobs.filter(
        (job) =>
          translateLabel(job.category, CATEGORY_LABELS, locale) ===
          activeCategory,
      )
    }
    if (!isAllRegions) {
      jobs = jobs.filter((job) => activeRegions.has(job.location))
    }

    return jobs
  }, [allJobs, activeCategory, activeRegions, isAllRegions, locale])

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / itemsPerPage))
  const paginatedJobs = filteredJobs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  )

  // Truncated page range (max 5 visible buttons)
  const pageRange = getPageRange(currentPage, totalPages)

  // Total counter text — split around {count} so the number can be bold
  const countStr = String(filteredJobs.length)
  const [totalPrefix, totalSuffix] = totalLabelTemplate.split("{count}")

  // URL sync helper — updates all relevant query params
  const updateUrl = useCallback(
    (params: { page?: number; category?: string; regions?: Set<string> }) => {
      const sp = new URLSearchParams(searchParams.toString())

      // Page
      const page = params.page ?? currentPage
      if (page <= 1) {
        sp.delete("page")
      } else {
        sp.set("page", String(page))
      }

      // Category
      const cat = params.category ?? activeCategory
      if (cat === ALL_KEY) {
        sp.delete("category")
      } else {
        sp.set("category", cat)
      }

      // Regions — multi-select support
      const regions = params.regions ?? activeRegions
      sp.delete("region")
      if (!regions.has(ALL_KEY)) {
        for (const r of regions) {
          sp.append("region", r)
        }
      }

      const query = sp.toString()
      router.replace(`${pathname}${query ? `?${query}` : ""}`, {
        scroll: false,
      })
    },
    [searchParams, router, pathname, currentPage, activeCategory, activeRegions],
  )

  const handleCategoryChange = useCallback(
    (_: unknown, value: string) => {
      setActiveCategory(value)
      setCurrentPage(1)
      updateUrl({ category: value, page: 1 })
    },
    [updateUrl],
  )

  const handleRegionClick = useCallback(
    (regionKey: string) => {
      let nextRegions: Set<string>

      if (regionKey === ALL_KEY) {
        nextRegions = new Set([ALL_KEY])
      } else {
        const next = new Set(activeRegions)
        next.delete(ALL_KEY)

        if (next.has(regionKey)) {
          next.delete(regionKey)
        } else {
          next.add(regionKey)
        }

        if (next.size === 0) {
          nextRegions = new Set([ALL_KEY])
        } else {
          nextRegions = next
        }
      }

      setActiveRegions(nextRegions)
      setCurrentPage(1)
      updateUrl({ regions: nextRegions, page: 1 })
    },
    [updateUrl, activeRegions],
  )

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page)
      updateUrl({ page })
      window.scrollTo({ top: 0, behavior: "smooth" })
    },
    [updateUrl],
  )

  // Pagination arrow handlers
  // In the design: > (right chevron) = next page, < (left chevron) = prev page
  // Due to RTL flex reversal, ChevronRight (source first) ends up visually on
  // the right, and ChevronLeft (source last) ends up visually on the left.
  const handlePrevPage = useCallback(() => {
    setCurrentPage((prev) => {
      const next = Math.max(1, prev - 1)
      updateUrl({ page: next })
      return next
    })
  }, [updateUrl])

  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => {
      const next = Math.min(totalPages, prev + 1)
      updateUrl({ page: next })
      return next
    })
  }, [totalPages, updateUrl])

  return (
    <Container paddingMobile>
      <SearchParamsListener onChange={setSearchParams} />
      <Root>
        {/* Hero section: title, subtitle, category tabs */}
        <InnerHero>
          <TextContainer>
            <TitleRow>
              <ChevronWrapper>
                <ChevronBullet />
              </ChevronWrapper>
              <PageTitle variant="h1" component="h2">{data.pageTitle}</PageTitle>
            </TitleRow>
            {data.pageSubtitle && (
              <PageSubtitle variant="body1">{data.pageSubtitle}</PageSubtitle>
            )}
          </TextContainer>

          {/* Category tabs — CMS-curated when set, else auto-surfaced from active job postings (EGDE-45) */}
          {categoryOptions.length > 0 && (
            <MuiTabs
              value={activeCategory}
              onChange={handleCategoryChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <MuiTab label={allLabel} value={ALL_KEY} />
              {/* EGDE-45: categoryOptions are already distinct display labels. */}
              {categoryOptions.map((cat) => (
                <MuiTab key={cat} label={cat} value={cat} />
              ))}
            </MuiTabs>
          )}
        </InnerHero>

        {/* Region filter chips — multi-select (ESEIM-296)
            Chips use flex-wrap, not flex-direction:row, so RTL does NOT
            reverse the chip order. Instead, chips wrap from the right edge
            and flow leftward per the writing direction. Place "All" chip
            FIRST in source so it appears first in RTL flow (rightmost). */}
        {regionOptions.length > 0 && (
          <FilterContainer>
            {regionsLabel && (
              <FilterLabel variant="body1">{regionsLabel}</FilterLabel>
            )}
            <ChipsContainer>
              <Chip
                $active={isAllRegions}
                onClick={() => handleRegionClick(ALL_KEY)}
              >
                <ChipText variant="body1">{allRegionLabel}</ChipText>
              </Chip>
              {regionOptions.map((region) => (
                <Chip
                  key={region}
                  $active={activeRegions.has(region)}
                  onClick={() => handleRegionClick(region)}
                >
                  <ChipText variant="body1">{translateLabel(region, REGION_LABELS, locale)}</ChipText>
                </Chip>
              ))}
            </ChipsContainer>
          </FilterContainer>
        )}

        {/* Total jobs counter */}
        <TotalCounter variant="body1">
          {totalPrefix}
          <BoldCount>{countStr}</BoldCount>
          {totalSuffix}
        </TotalCounter>

        {/* Job cards grid */}
        <CardGrid>
          {paginatedJobs.map((job) => (
            <JobCard
              key={job.slug}
              as={Link}
              href={getLocalizedUrl(`/careers/${job.slug}`, locale)}
            >
              <CardContent>
                <CardHeading>
                  <CardTitle variant="body1">{job.title}</CardTitle>
                  <CardExcerpt variant="body2">{job.excerpt}</CardExcerpt>
                </CardHeading>
              </CardContent>
              {/* Card footer: source [LocationRow, CtaLink]
                  RTL visual: [CtaLink LEFT, LocationRow RIGHT] */}
              <CardFooter>
                {/* Location: source [Text, Icon]
                    RTL visual: [Icon LEFT, Text RIGHT] — matches Figma */}
                <LocationRow>
                  <LocationText variant="body1">{translateLabel(job.location, REGION_LABELS, locale)}</LocationText>
                  <LocationIcon>
                    <LocationPin width={14} height={14} />
                  </LocationIcon>
                </LocationRow>
                {/* CTA link: source [CtaText, CtaArrow]
                    RTL visual: [Arrow LEFT, Text RIGHT] */}
                <CtaLink>
                  <CtaText variant="body1">{ctaLabel}</CtaText>
                  <CtaArrow>
                    <ArrowLeft width={11} height={8} />
                  </CtaArrow>
                </CtaLink>
              </CardFooter>
            </JobCard>
          ))}
        </CardGrid>

        {/* Pagination
            Source: [ChevronRight, pages, ChevronLeft]
            RTL visual: [< LEFT, pages, > RIGHT]
            RTL navigation: > (RIGHT) = prev, < (LEFT) = next */}
        {totalPages > 1 && (
          <PaginationContainer>
            <PaginationArrow
              onClick={handlePrevPage}
              disabled={currentPage <= 1}
              aria-label="Previous page"
            >
              <ChevronRight width={7} height={16} />
            </PaginationArrow>

            {pageRange.map((item) => (
              <PageButton
                key={item}
                $active={item === currentPage}
                onClick={() => handlePageChange(item)}
                aria-label={`Page ${item}`}
                aria-current={item === currentPage ? "page" : undefined}
              >
                <PageButtonText $active={item === currentPage}>
                  {item}
                </PageButtonText>
              </PageButton>
            ))}

            <PaginationArrow
              onClick={handleNextPage}
              disabled={currentPage >= totalPages}
              aria-label="Next page"
            >
              <ChevronLeft width={7} height={16} />
            </PaginationArrow>
          </PaginationContainer>
        )}
      </Root>
    </Container>
  )
}
